import { useState } from "react";
import { Dialog } from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

import { FaLink } from "react-icons/fa6";
import { FaPhoenixFramework } from "react-icons/fa";
import { BsFillPeopleFill } from "react-icons/bs";
import { TbCreditCardPay } from "react-icons/tb";
import { FaMoneyCheckDollar } from "react-icons/fa6";
import { FaRegCopy } from "react-icons/fa";
import { FaUserTimes } from "react-icons/fa";

import { copyToClipboard } from "../../containers/Raydium/utils";
import logo from '../../assets/images/logo.webp';
import SocialMediaLinks from "../SocialMediaLinks/SocialMediaLinks";
import { useSelector } from "react-redux";
import { selectUserSessionData } from "../../lib/userSessionSlice";

export default function UserWallet({ currentWallet, disconnect, publicKey }) {
    const [open, setOpen] = useState(false);
    const userData = useSelector(selectUserSessionData);
    let penndingRef = userData?.referredUsers?.filter((ref) => ref?.tokenBalance === 0).length;

    const handleDisconnect = async () => {
        setOpen(false);
        disconnect();
    };

    const menu = [
        {
            title: 'Pending Referrals',
            desc: 'Pending Users',
            value: penndingRef || 0,
            icon: <FaUserTimes className="text-2xl text-neutral-300" />
        },
        {
            title: 'Total Referrals',
            desc: 'LifeTime Referrals',
            value: userData?.referredUsers?.length || 0,
            icon: <BsFillPeopleFill className="text-2xl text-blue-500" />
        },
        {
            title: 'Total Withdrawals',
            desc: 'LifeTime Withdrawals',
            value: userData?.claimedBalance?.toFixed(2) || 0,
            icon: <TbCreditCardPay className="text-2xl text-green-500" />
        },
        {
            title: 'Total Purchased',
            desc: 'Your Phoenix Tokens + bonus',
            value: (userData?.totalTokenBalance)?.toFixed() || 0,
            icon: <FaPhoenixFramework className="text-2xl text-orange-500" />
        },
        {
            title: 'Total Profits',
            desc: 'LifeTime Generated Profits',
            value: (userData?.referralBalance + userData?.claimedBalance)?.toFixed(2) + " SOL" || 0,
            icon: <FaMoneyCheckDollar className="text-2xl text-teal-500" />
        },
    ];

    function Navigation() {
        return (
            <div className="flex gap-2 w-full flex-wrap">
                {menu.map((item, key) => (
                    <div key={key} className="w-max h-max flex justify-between items-center bg-yellow-500/10 border border-yellow-800/20 p-2 rounded space-y-1 grow">
                        <div className="grid items-center gap-1">
                            <p className="text-xs font-semibold text-orange-500">{item.title}</p>
                            <p className="text-lg font-bold">{item.value}</p>
                            <p className="text-xxs text-neutral-500">{item.desc}</p>
                        </div>
                        {item?.icon}
                    </div>
                ))}
            </div>
        );
    }

    return (
        <>
            {/* Button to Open Panel */}
            <div
                onClick={() => setOpen(true)}
                className="flex gap-2 items-center cursor-pointer bg-yellow-500 border border-neutral-800 px-4 py-2 rounded hover:bg-yellow-400"
            >
                <img
                    src={currentWallet?.[0]?.adapter?.icon}
                    alt=""
                    className="w-6 h-6 rounded-full outline outline-black shadow-xl"
                />
                <div className="grid">
                    <p className="text-black text-xs font-bold">
                        {publicKey?.toBase58()?.slice(0, 4) +
                            "..." +
                            publicKey?.toBase58()?.slice(40, publicKey.toBase58().length)}
                    </p>
                </div>
            </div>

            {/* Side Panel using Framer Motion */}
            <AnimatePresence>
                {open && (
                    <Dialog open={open} onClose={() => setOpen(false)} className="fixed inset-0 z-50">
                        {/* Background Overlay */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                            onClick={() => setOpen(false)}
                        />

                        {/* Sliding Panel */}
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "spring", stiffness: 250, damping: 25 }}
                            className="fixed inset-y-0 right-0 max-w-lg bg-neutral-900/70 border-l border-neutral-800 shadow-xl p-5 backdrop-blur"
                        >
                            <button
                                onClick={() => setOpen(false)}
                                className="absolute text-white top-4 right-4 text-xl"
                            >
                                ✖
                            </button>
                            <div className="w-full h-full flex flex-col justify-between">
                                <div className="flex flex-col space-y-10">
                                    {/* Panel Content */}
                                    <div className="flex items-center gap-2 justify-start w-full">
                                        <h2 className="text-lg font-semibold text-white">Wallet</h2>
                                        <p
                                            className="font-semibold text-xxs text-neutral-400 bg-neutral-800 px-3 py-1 rounded flex items-center gap-2 cursor-pointer hover:bg-neutral-700"
                                            onClick={() => copyToClipboard(publicKey?.toBase58())}
                                        >
                                            <FaRegCopy className="text-md" />
                                            {publicKey?.toBase58()}
                                        </p>
                                    </div>

                                    <div className="flex flex-col items-center gap-5 w-full">
                                        <h1 className="text-5xl font-bold text-white">
                                            {userData?.solBalance?.toFixed(2)} <span className="text-md font-bold">SOL</span>
                                        </h1>
                                        <div className="flex items-center">
                                            <h1 className="font-bold text-green-500">
                                                {userData?.totalTokenBalance?.toFixed()} PHX
                                            </h1>
                                            <img src={logo} alt="" className="w-10 h-10" />
                                        </div>
                                        <hr className="border border-yellow-500/10 w-full" />
                                        <Navigation />
                                    </div>
                                    <hr className="border border-yellow-500/10" />

                                    <div className="flex gap-5 w-full items-center justify-center">
                                        <Link to={'/dashboard'} className="bg-neutral-800/80 py-2 px-4 rounded">
                                            Dashboard
                                        </Link>
                                        <Link to={'/ico'} className="bg-neutral-800/80 py-2 px-4 rounded">
                                            Buy Phoenix
                                        </Link>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-5 items-center justify-center w-full">
                                    <button
                                        onClick={handleDisconnect}
                                        className="w-full text-center font-semibold text-sm gap-2 items-center cursor-pointer bg-yellow-500 hover:bg-yellow-400 border border-neutral-800 px-4 py-2 rounded"
                                    >
                                        Disconnect
                                    </button>
                                    <SocialMediaLinks />
                                </div>
                            </div>
                        </motion.div>
                    </Dialog>
                )}
            </AnimatePresence>
        </>
    );
}